/**
 * Query options factories for user-created trackers and their items.
 */
import {
  TrackersService,
  type OrganizationTrackerDto,
  type TrackerScopeDto,
  type TrackerItemResponseDto,
  type WorkspaceResolutionResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { BOARD_ROW_CAP, ITEMS_PAGE_SIZE } from '@/shared/constants/tracker-board.constants';

import { TRACKER_QUERY_KEYS } from './query-keys';

export const trackersListQueryOptions = (workspaceId: string) =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
    queryFn: async ({ signal }) => {
      const response = await TrackersService.list({ client: workspaceHttp, signal });
      return response.data?.items ?? [];
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });

const ORGANIZATION_TRACKERS_PAGE_SIZE = 100;

/**
 * Trackers from every workspace of the organization the person can open, all
 * pages of them: the hub searches, sorts and filters them on the client.
 */
export const organizationTrackersQueryOptions = () =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.organization(),
    queryFn: async ({ signal }) => {
      const trackers: OrganizationTrackerDto[] = [];
      let cursor: string | undefined;
      do {
        const response = await TrackersService.listOrganizationTrackers({
          client: workspaceHttp,
          query: { pageSize: ORGANIZATION_TRACKERS_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
          signal,
        });
        if (!response.data) throw new Error('organization trackers returned an empty body');
        trackers.push(...response.data.data);
        cursor = response.data.hasMore ? (response.data.nextCursor ?? undefined) : undefined;
      } while (cursor);
      return trackers;
    },
    staleTime: 30_000,
  });

/**
 * The workspace a tracker lives in, asked before the board loads so a link to
 * a tracker in another workspace switches to it rather than 404ing.
 */
export const trackerResolutionQuery = (trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.resolution(trackerId),
    queryFn: async (): Promise<WorkspaceResolutionResponseDto> => {
      const response = await TrackersService.resolveTrackerWorkspace({
        client: workspaceHttp,
        path: { trackerId },
        // The board shows its own not-found state; the global toast would come first.
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      if (!response.data) throw new Error('Failed to resolve tracker');
      return response.data;
    },
    // A tracker never changes workspace, so one answer holds for the session.
    staleTime: Infinity,
    retry: false,
  });

export const trackerQueryOptions = (workspaceId: string, trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.detail(workspaceId, trackerId),
    queryFn: async ({ signal }) => {
      const response = await TrackersService.getById({
        client: workspaceHttp,
        path: { trackerId },
        signal,
      });
      if (!response.data) throw new Error('Tracker not found');
      return response.data;
    },
    enabled: Boolean(workspaceId) && Boolean(trackerId),
    staleTime: 30_000,
  });

export const trackerItemsQueryOptions = (workspaceId: string, trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.items(workspaceId, trackerId),
    queryFn: async ({ signal }) => {
      // Accumulate pages until the full count or the safety cap. Small boards
      // resolve in a single request (the loop exits once `items >= total`).
      const items: TrackerItemResponseDto[] = [];
      for (let skip = 0; ; skip += ITEMS_PAGE_SIZE) {
        const response = await TrackersService.listItems({
          client: workspaceHttp,
          path: { trackerId },
          query: { skip, take: ITEMS_PAGE_SIZE },
          signal,
        });
        const page = response.data?.items ?? [];
        items.push(...page);
        const total = response.data?.total ?? items.length;
        if (page.length === 0 || items.length >= total || items.length >= BOARD_ROW_CAP) break;
      }
      return items;
    },
    enabled: Boolean(workspaceId) && Boolean(trackerId),
  });

/**
 * Everything the rows endpoint narrows, counts and orders by. Held as one
 * object because it is also the cache key: two requests that differ anywhere
 * here describe different sets, and a count from one must never be shown
 * against rows from the other.
 */
export interface TrackerRowsParams {
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** `field:operator[:value]`, repeatable; same field ORs, different fields AND. */
  filter?: readonly string[];
  /** Fields to return whole-set value counts for. The server allows eight. */
  countBy?: readonly string[];
  /**
   * `board`, `all` or comma-separated workspace ids. Always sent: the server
   * reads a missing scope as the stored one, not as the board alone.
   */
  scope?: string;
}

export const TRACKER_ROWS_PAGE_SIZE = 100;

/**
 * Paged rows for a record-backed board, with the server's own totals and value
 * counts riding along on every page. Deliberately stale on arrival: a board
 * reads live records, so it refetches on open and on window focus rather than
 * showing whatever was true when the tab was last looked at. No polling.
 */
export const trackerRowsQueryOptions = (
  workspaceId: string,
  trackerId: string,
  params: TrackerRowsParams,
) =>
  infiniteQueryOptions({
    queryKey: TRACKER_QUERY_KEYS.rows(workspaceId, trackerId, params),
    queryFn: async ({ pageParam, signal }) => {
      const response = await TrackersService.listRows({
        client: workspaceHttp,
        path: { trackerId },
        query: {
          pageSize: TRACKER_ROWS_PAGE_SIZE,
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(params.search ? { search: params.search } : {}),
          ...(params.sortBy ? { sortBy: params.sortBy } : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
          ...(params.filter?.length ? { filter: [...params.filter] } : {}),
          ...(params.countBy?.length ? { countBy: [...params.countBy] } : {}),
          ...(params.scope ? { scope: params.scope } : {}),
        },
        signal,
      });
      if (!response.data) throw new Error('tracker rows returned an empty body');
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    enabled: Boolean(workspaceId) && Boolean(trackerId),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

export const trackerMembersQueryOptions = (workspaceId: string, trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.members(workspaceId, trackerId),
    queryFn: async ({ signal }) => {
      const response = await TrackersService.listMembers({
        client: workspaceHttp,
        path: { trackerId },
        signal,
      });
      return response.data ?? { ownerId: '', userIds: [], members: [] };
    },
    enabled: Boolean(workspaceId) && Boolean(trackerId),
  });

/**
 * Which workspaces a record-backed board reads rows from. The workspaces the
 * person could add come along only with `withAvailable`, or when the scope
 * already reads beyond the board: the server checks each one with the platform.
 */
export const trackerScopeQueryOptions = (
  workspaceId: string,
  trackerId: string,
  { withAvailable = false }: { withAvailable?: boolean } = {},
) =>
  queryOptions({
    queryKey: TRACKER_QUERY_KEYS.scope(workspaceId, trackerId, { withAvailable }),
    queryFn: async ({ signal }): Promise<TrackerScopeDto> => {
      const response = await TrackersService.getScope({
        client: workspaceHttp,
        path: { trackerId },
        ...(withAvailable ? { query: { include: 'available' } } : {}),
        signal,
      });
      if (!response.data) throw new Error('tracker scope returned an empty body');
      return response.data;
    },
    enabled: Boolean(workspaceId) && Boolean(trackerId),
    staleTime: 30_000,
  });
